import { Spacing } from '@persona-id/design-tokens';
import { sortBy } from 'lodash/fp';
import { useMemo } from 'react';
import { Control, Controller } from 'react-hook-form';
import { connect, ConnectedProps } from 'react-redux';
import styled from 'styled-components';

import FormControlLabel from 'dashboard/components/FormControls/FormControlLabel';
import ReactSelect from 'dashboard/components/ReactSelect';
import HelpIcon from 'dashboard/components-lib/HelpIcon';
import FormGroup from 'dashboard/layouts/FormLayout/FormGroup';
import { MessageType } from 'dashboard/lib/interfaces/Message';
import { AccountTypesFilterOption } from 'dashboard/models/account-type';
import { InquiryTemplateFilterOption } from 'dashboard/models/inquiry-template';
import { TemplateFilterOption } from 'dashboard/models/template';
import { Theme } from 'dashboard/models/theme';
import { showMessage } from 'dashboard/redux/actions/messages';
import { RootState } from 'dashboard/redux/store';
import { loadThemeSets } from 'dashboard/redux/thunk-actions/theme-sets';
import Checkbox from 'design-system/components/Checkbox';
import { Input } from 'design-system/components/Input';
import Select, { Option } from 'design-system/components/Select';
import Sticker from 'design-system/components/Sticker';
import useApiLoader from 'lib/hooks/useApiLoader';
import { ApiModel } from 'lib/interfaces/api';
import { isPermissionsError, permissionsErrorMessage } from 'lib/permissions-errors';

const OptionValueWrapper = styled.div`
  display: flex;
  gap: ${Spacing[2]};
  align-items: center;
`;

const TemplateFormGroup = ({
  templateFilterOptions,
  control,
  submitting,
}: {
  templateFilterOptions: (InquiryTemplateFilterOption | TemplateFilterOption)[];
  control: Control;
  submitting: boolean;
}) => {
  return (
    <FormGroup>
      <FormControlLabel
        id='create-inquiry__select-template-id'
        htmlFor='create-inquiry__select-template-id'
      >
        Select a template
      </FormControlLabel>
      <Controller
        name='templateId'
        control={control}
        render={({ field }) => (
          <ReactSelect
            {...field}
            id='create-inquiry__select-template-id'
            aria-labelledby='create-inquiry__select-template-id'
            disabled={submitting}
            options={templateFilterOptions
              .map((basic) => {
                const isDraft =
                  basic.type === ApiModel.InquiryTemplate && !basic['published-version-id'];

                return {
                  label: isDraft ? `${basic.name} (Draft)` : basic.name,
                  value: basic.id,
                  disabled: isDraft,
                };
              })
              .sort((a, b) => a.label.localeCompare(b.label))}
            portal
          />
        )}
      />
    </FormGroup>
  );
};

const ReferenceIdFormGroup = ({
  control,
  submitting,
}: {
  control: Control;
  submitting: boolean;
}) => {
  return (
    <FormGroup>
      <span>
        <FormControlLabel
          htmlFor='create-inquiry__input-reference-id'
          id='create-inquiry__input-reference-id'
        >
          Reference ID (optional)
        </FormControlLabel>
        <HelpIcon>
          To group and search for related inquiries, you can specify a reference ID for each
          inquiry. The reference ID can be used as a unique identifier to tie back to your internal
          system along with merging inquiries into a single account within Persona.
        </HelpIcon>
      </span>
      <Controller
        name='referenceId'
        control={control}
        render={({ field: { value, onChange } }) => (
          <Input
            id='create-inquiry__input-reference-id'
            aria-labelledby='create-inquiry__input-reference-id'
            placeholder='Reference ID'
            disabled={submitting}
            value={value}
            onChange={(e) => {
              onChange(e.target.value);
            }}
          />
        )}
      />
    </FormGroup>
  );
};

const AccountTypeFormGroup = ({
  accountTypeFilterOptions,
  control,
  submitting,
}: {
  accountTypeFilterOptions: AccountTypesFilterOption[];
  control: Control;
  submitting: boolean;
}) => {
  const defaultAccountTypeFilterOptions = sortBy(
    (option) => option.name,
    accountTypeFilterOptions.filter((option) => option['is-legacy-account-type']),
  );
  const nonDefaultAccountTypeFilterOptions = sortBy(
    (option) => option.name,
    accountTypeFilterOptions.filter((option) => !option['is-legacy-account-type']),
  );
  const orderedAccountTypeFilterOptions = [
    ...defaultAccountTypeFilterOptions,
    ...nonDefaultAccountTypeFilterOptions,
  ];

  return (
    <FormGroup>
      <FormControlLabel htmlFor='create-inquiry__select-account-type-id'>
        Select an account type
      </FormControlLabel>
      <Controller
        name='accountTypeId'
        control={control}
        render={({ field: { value, onChange } }) => (
          <Select
            id='create-inquiry__select-account-type-id'
            value={value}
            onValueChange={onChange}
            aria-labelledby='create-inquiry__select-account-type-id'
            placeholder='Select...'
            disabled={submitting}
          >
            {orderedAccountTypeFilterOptions.map((option, idx) => {
              let suffixIcon;
              if (option['is-legacy-account-type']) {
                suffixIcon = <Sticker variant='primary'>Default</Sticker>;
              }

              return (
                <Option
                  key={idx}
                  value={option.id}
                  // Option is not accessible within a dialog (i.e. modal). If portal
                  // is used, option is rendered accessible but not visually shown
                  data-test={`account-type-select__option-${option.name}`}
                >
                  <OptionValueWrapper>
                    {option.name}
                    {suffixIcon}
                  </OptionValueWrapper>
                </Option>
              );
            })}
          </Select>
        )}
      />
    </FormGroup>
  );
};

const ThemeSetFormGroup = ({
  control,
  submitting,
  themeSets,
  loadThemeSets,
}: {
  control: Control;
  submitting: boolean;
} & PropsFromRedux) => {
  const { loading: isLoading } = useApiLoader({
    loader: async () =>
      await loadThemeSets({
        omni: undefined,
        sortParams: { sort: 'updated_at', shouldSortDescending: true },
      }),
    deps: [loadThemeSets],
    onError: (error) => {
      if (isPermissionsError(error)) {
        showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        showMessage('Failed to load themes.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      showMessage('We are experiencing network issues. Please try again.', MessageType.Failure),
  });

  const themeSetOptions = useMemo(
    () =>
      themeSets.map((themeSet) => ({
        value: themeSet.id,
        label: `${themeSet.attributes.name} (${themeSet.id})`,
      })),
    [themeSets],
  );

  const placeholderValue = useMemo(() => {
    if (isLoading) {
      return 'Loading themes';
    } else if (themeSets.length === 0) {
      return 'No themes available';
    } else {
      return 'Theme Option';
    }
  }, [isLoading, themeSets]);

  return (
    <FormGroup>
      <FormControlLabel
        id='create-inquiry__select-theme-set-id'
        htmlFor='create-inquiry__select-theme-set-id'
      >
        Select a Theme (optional)
      </FormControlLabel>
      <Controller
        name='themeSetId'
        control={control}
        render={({ field: { value, onChange } }) => (
          <ReactSelect
            value={value}
            id='create-inquiry__select-theme-set-id'
            aria-labelledby='create-inquiry__select-theme-set-id'
            options={themeSetOptions}
            disabled={submitting || isLoading}
            onChange={onChange}
            placeholder={placeholderValue}
            isSearchable
            portal
          />
        )}
      />
    </FormGroup>
  );
};

const mapStateToProps = (state: RootState) => {
  return {
    themeSets: state.themeSets.themeSets,
  };
};

const mapDispatchToProps = {
  loadThemeSets,
  showMessage,
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;
export const ConnectedThemeSetFormGroup = connector(ThemeSetFormGroup);

const ThemeFormGroup = ({
  themes,
  control,
  submitting,
}: {
  themes: Theme[];
  control: Control;
  submitting: boolean;
}) => {
  return (
    <FormGroup>
      <FormControlLabel
        id='create-inquiry__select-theme-id'
        htmlFor='create-inquiry__select-theme-id'
      >
        Select a theme
      </FormControlLabel>
      <Controller
        name='themeId'
        control={control}
        render={({ field }) => (
          <ReactSelect
            {...field}
            id='create-inquiry__select-theme-id'
            aria-labelledby='create-inquiry__select-theme-id'
            disabled={submitting}
            options={themes.map((theme) => ({ label: theme.attributes.name, value: theme.id }))}
            portal
          />
        )}
      />
    </FormGroup>
  );
};

const SendEmailFormGroup = ({ control, submitting }: { control: Control; submitting: boolean }) => {
  return (
    <Controller
      name='sendEmail'
      control={control}
      render={({ field: { value, onChange } }) => (
        <Checkbox
          id='create-inquiry__input-allow-send-email'
          label='Send email to recipient'
          name='sendEmailAddress'
          disabled={submitting}
          checked={value}
          onChange={() => onChange(!value)}
          data-test='create-inquiry__input-allow-send-email'
        />
      )}
    />
  );
};

export {
  TemplateFormGroup,
  ReferenceIdFormGroup,
  AccountTypeFormGroup,
  ThemeFormGroup,
  SendEmailFormGroup,
};
